import get from "lodash/get";
import isNil from "lodash/isNil";
import merge from "lodash/merge";
import omitBy from "lodash/omitBy";

import { deepCloneAndOmitAtPath } from "../../helpers/state";
import * as configureWevoActionTypes from "./types";
import * as configureWevoConstants from "./constants";
import { sessionTypes } from "../session";

const initialState = {};

/**
 * Get a copy of the openQuotas set for the given wevoId.
 * @param state a valid state object for this reducer.
 * @param wevoId the unique id of the wevo being edited.
 * @returns {Set<number>} a copy of the set.
 */
function audienceOpenQuotasForWevoId(state, wevoId) {
    const currentSet = get(state, [
        configureWevoConstants.SELECT_AUDIENCE_REDUCER_KEY,
        wevoId,
        configureWevoConstants.AUDIENCE_OPEN_QUOTAS
    ]);
    return currentSet ? new Set(currentSet) : new Set();
}

/**
 * Get a copy of the inFlightRequests set for the given wevoId.
 * @param state a valid state object for this reducer.
 * @param wevoId the unique id of the wevo being edited.
 * @returns {Set<number>} a copy of the set.
 */
function audienceInFlightRequestsForWevoId(state, wevoId) {
    const currentSet = get(state, [
        configureWevoConstants.SELECT_AUDIENCE_REDUCER_KEY,
        wevoId,
        configureWevoConstants.AUDIENCE_IN_FLIGHT_REQUESTS
    ]);
    return currentSet ? new Set(currentSet) : new Set();
}

export default function configureWevo(state = initialState, action) {
    switch (action.type) {
        case sessionTypes.RESET_AUTH:
            return initialState;

        /*
         Create and update wevo broadcasts
         */
        case configureWevoActionTypes.REQUEST_CREATE_WEVO:
            return merge({}, state, {
                [configureWevoConstants.PROJECT_DEFINITION_REDUCER_KEY]: {
                    createNew: {
                        status: action.type
                    }
                }
            });

        case configureWevoActionTypes.CREATE_WEVO_SUCCESS:
            return merge({}, state, {
                [configureWevoConstants.PROJECT_DEFINITION_REDUCER_KEY]: {
                    createNew: {
                        status: action.type,
                        wevoId: action.wevoId
                    }
                }
            });

        case configureWevoActionTypes.CREATE_WEVO_ERROR:
            return merge({}, state, {
                [configureWevoConstants.PROJECT_DEFINITION_REDUCER_KEY]: {
                    createNew: {
                        status: action.type,
                        error: action.error
                    }
                }
            });

        case configureWevoActionTypes.REQUEST_SAVE_WEVO:
            return merge({}, state, {
                [configureWevoConstants.PROJECT_DEFINITION_REDUCER_KEY]: {
                    [action.wevoId]: {
                        status: action.type
                    }
                }
            });

        case configureWevoActionTypes.SAVE_WEVO_ERROR:
            return merge({}, state, {
                [configureWevoConstants.PROJECT_DEFINITION_REDUCER_KEY]: {
                    [action.wevoid]: {
                        status: action.type,
                        error: action.error
                    }
                }
            });

        case configureWevoActionTypes.SAVE_WEVO_SUCCESS:
            return deepCloneAndOmitAtPath(state, [
                configureWevoConstants.PROJECT_DEFINITION_REDUCER_KEY,
                action.wevoId
            ]);

        case configureWevoActionTypes.CLEAR_STATUS_STATE:
            return deepCloneAndOmitAtPath(state, action.removalPath);

        /*

         Create and update page broadcasts

         */
        case configureWevoActionTypes.REQUEST_CREATE_PAGE:
            return merge({}, state, {
                [configureWevoConstants.SELECT_PAGES_REDUCER_KEY]: {
                    activeUploads: {
                        [action.sortOrder]: {
                            status: action.type,
                            name: action.name
                        }
                    }
                }
            });

        case configureWevoActionTypes.CREATE_PAGE_ERROR:
            return merge({}, state, {
                [configureWevoConstants.SELECT_PAGES_REDUCER_KEY]: {
                    activeUploads: {
                        [action.sortOrder]: {
                            status: action.type,
                            error: action.error
                        }
                    }
                }
            });

        case configureWevoActionTypes.REMOVE_SELECTED_PAGE_ERROR_CARD:
            /*
              This handles when the user wants to remove a PageUpload card that has
              returned an error. It is a special case since any other page removal
              would expect a pageId.

              Users can remove errored cards directly (with their sort order) or clear them out
              all at once by not passing a sort order (helpful for when the select pages component
              unmounts)
             */
            const { activeUploadIndex } = action;
            const selectPagesState =
                state[configureWevoConstants.SELECT_PAGES_REDUCER_KEY];
            if (!selectPagesState) {
                // Because we clear all pages in error states when the SelectPages component unmounts,
                // there isn't guaranteed to be a valid object at state.SELECT_PAGES_REDUCER_KEY.
                // If the user were to navigate to SelectPages and unmount without making any edits,
                // this key wouldn't exist, but the componentWillUnmount will still try to clear all the errors
                return state;
            }
            const activeUploads = selectPagesState.activeUploads || {};
            let activeUploadsClone = { ...activeUploads };
            if (!isNil(activeUploadIndex)) {
                // If the user specified a specific error card to remove
                delete activeUploadsClone[activeUploadIndex];
            } else {
                // Remove all error cards
                activeUploadsClone = omitBy(activeUploadsClone, upload => {
                    return !isNil(upload.error);
                });
            }

            /*
              Replace the current SelectPages state with the new one. A merge would cause
              removed pages to re-appear, so it needs to be a full replacement.
             */
            return {
                ...state,
                [configureWevoConstants.SELECT_PAGES_REDUCER_KEY]: {
                    ...state[configureWevoConstants.SELECT_PAGES_REDUCER_KEY],
                    activeUploads: activeUploadsClone
                }
            };

        case configureWevoActionTypes.CREATE_PAGE_SUCCESS:
            const { sortOrder } = action;
            return deepCloneAndOmitAtPath(state, [
                configureWevoConstants.SELECT_PAGES_REDUCER_KEY,
                "activeUploads",
                sortOrder
            ]);

        case configureWevoActionTypes.REMOVE_PAGE_SUCCESS:
            return deepCloneAndOmitAtPath(state, [
                configureWevoConstants.SELECT_PAGES_REDUCER_KEY,
                action.pageId
            ]);

        case configureWevoActionTypes.REQUEST_UPDATE_PAGE:
            return merge({}, state, {
                [configureWevoConstants.SELECT_PAGES_REDUCER_KEY]: {
                    [action.pageId]: {
                        status: action.type
                    }
                }
            });

        case configureWevoActionTypes.UPDATE_PAGE_ERROR:
            return merge({}, state, {
                [configureWevoConstants.SELECT_PAGES_REDUCER_KEY]: {
                    [action.pageId]: {
                        status: action.type
                    }
                }
            });

        case configureWevoActionTypes.UPDATE_PAGE_SUCCESS:
            return merge({}, state, {
                [configureWevoConstants.SELECT_PAGES_REDUCER_KEY]: {
                    [action.pageId]: {
                        status: action.type
                    }
                }
            });

        /*

         Create and remove CTA actions

         */

        case configureWevoActionTypes.REQUEST_CREATE_CTA:
        case configureWevoActionTypes.REQUEST_REMOVE_CTA:
            return merge({}, state, {
                [configureWevoConstants.SELECT_CTAS_REDUCER_KEY]: {
                    [action.wevoId]: {
                        status: action.type
                    }
                }
            });

        case configureWevoActionTypes.REMOVE_CTA_SUCCESS:
        case configureWevoActionTypes.REMOVE_CTA_ERROR:
        case configureWevoActionTypes.CREATE_CTA_SUCCESS:
        case configureWevoActionTypes.CREATE_CTA_ERROR:
            return deepCloneAndOmitAtPath(state, [
                configureWevoConstants.SELECT_CTAS_REDUCER_KEY,
                action.wevoId
            ]);

        /*

         Create and update audience broadcasts

         */

        case configureWevoActionTypes.REQUEST_ATTACH_AUDIENCE: {
            // make sure the quota editor box starts off closed
            const openQuotas = audienceOpenQuotasForWevoId(
                state,
                action.wevoId
            );
            openQuotas.delete(action.audienceId);

            // flag that this audience has a request in flight
            const inFlightRequests = audienceInFlightRequestsForWevoId(
                state,
                action.wevoId
            );
            inFlightRequests.add(action.audienceId);

            return merge({}, state, {
                [configureWevoConstants.SELECT_AUDIENCE_REDUCER_KEY]: {
                    [action.wevoId]: {
                        inFlightRequests,
                        openQuotas
                    }
                }
            });
        }

        case configureWevoActionTypes.ATTACH_AUDIENCE_ERROR:
        case configureWevoActionTypes.ATTACH_AUDIENCE_SUCCESS: {
            const inFlightRequests = audienceInFlightRequestsForWevoId(
                state,
                action.wevoId
            );
            inFlightRequests.delete(action.audienceId);

            return merge({}, state, {
                [configureWevoConstants.SELECT_AUDIENCE_REDUCER_KEY]: {
                    [action.wevoId]: {
                        inFlightRequests
                    }
                }
            });
        }

        case configureWevoActionTypes.REQUEST_DETACH_AUDIENCE: {
            const inFlightRequests = audienceInFlightRequestsForWevoId(
                state,
                action.wevoId
            );
            inFlightRequests.add(action.audienceId);

            return merge({}, state, {
                [configureWevoConstants.SELECT_AUDIENCE_REDUCER_KEY]: {
                    [action.wevoId]: {
                        inFlightRequests
                    }
                }
            });
        }

        case configureWevoActionTypes.DETACH_AUDIENCE_ERROR:
        case configureWevoActionTypes.DETACH_AUDIENCE_SUCCESS: {
            const inFlightRequests = audienceInFlightRequestsForWevoId(
                state,
                action.wevoId
            );
            inFlightRequests.delete(action.audienceId);

            return merge({}, state, {
                [configureWevoConstants.SELECT_AUDIENCE_REDUCER_KEY]: {
                    [action.wevoId]: {
                        inFlightRequests
                    }
                }
            });
        }

        case configureWevoActionTypes.TOGGLE_QUOTA_EDITOR_FOR_AUDIENCE: {
            const openQuotas = audienceOpenQuotasForWevoId(
                state,
                action.wevoId
            );
            if (openQuotas.has(action.audienceId)) {
                openQuotas.delete(action.audienceId);
            } else {
                openQuotas.add(action.audienceId);
            }

            const wevoState = get(state, [
                configureWevoConstants.SELECT_AUDIENCE_REDUCER_KEY,
                action.wevoId
            ]);
            return merge({}, state, {
                [configureWevoConstants.SELECT_AUDIENCE_REDUCER_KEY]: {
                    [action.wevoId]: {
                        ...wevoState,
                        openQuotas
                    }
                }
            });
        }

        default:
            return state;
    }
}



// WEBPACK FOOTER //
// ./src/modules/configure-wevo/reducers.js